/**
 * QA Panel — synthetic jackpot win-statistics builder.
 *
 * Produces the MAPPED feature shape (`IJackpotWinsData`) that
 * `mapJackpotWinStatsResponse` emits, not the raw BE payload — the statistics drawer's selectors
 * consume the mapped type, so building it directly keeps the mock honest without duplicating the
 * BE-to-feature mapping.
 *
 * Everything the drawer actually depends on is reproduced faithfully:
 *   - level names use the real vocabulary (`Grand` / `Major` / `Minor` / `Mini`), so the wordmark
 *     artwork (`statisticsAssets`) and the per-level accent colours resolve;
 *   - `lastWins` is genuinely newest-first and `topWins` genuinely amount-desc, and the two
 *     orderings genuinely differ — otherwise the LAST 100 / TOP 10 switch looks broken;
 *   - `topWins` is capped at 10 entries, exactly like BE sends it;
 *   - amounts are whole minor units, exactly like BE sends them (display divides at render);
 *   - `gameKey`s come from `appStore.supportedGames`, so row thumbnails render and the
 *     game-launch click works;
 *   - player ids are ALREADY masked (`06*65`) — the lobby never masks client-side.
 *
 * Lives in the qaPanel (not in `features/jackpots`) so it is dead-stripped from production
 * bundles along with the rest of the panel. Only the mock STATE and its actions live in the
 * feature store — same split as `buildMockPrizePopCampaign` vs `campaignsStore.addMockCampaign`.
 */

import {
    JACKPOT_LEVEL_IDS,
    JACKPOT_STATISTICS_LAST_WINS_LIMIT,
    JACKPOT_STATISTICS_TOP_WINS_LIMIT,
    JACKPOT_WIN_MINOR_UNITS,
    isPremiumLinkJackpot,
} from '../../jackpots';
import type { IJackpotWinLevel, IJackpotWinRow, IJackpotWinsData } from '../../jackpots';
import { useAppStore } from '../../../store/app/appStore';

/**
 * The drawer's level vocabulary, in display order. Title case rather than the panel's uppercase
 * `JACKPOT_LEVEL_LABELS`, because that is how BE spells level names and how
 * `JACKPOT_STATISTICS_BOOST_BASE_LEVELS` / the wordmark asset lookup expect them.
 */
const LEVEL_NAME_BY_ID: Record<number, string> = {
    [JACKPOT_LEVEL_IDS.GRAND]: 'Grand',
    [JACKPOT_LEVEL_IDS.MAJOR]: 'Major',
    [JACKPOT_LEVEL_IDS.MINOR]: 'Minor',
    [JACKPOT_LEVEL_IDS.MINI]: 'Mini',
};

/**
 * Premium Link does NOT use the vocabulary above: its tiers are called Super / Middle / Mini by
 * the real win-stats endpoint, and both the wordmark lookup and the chip palette key off those
 * names — so a mock that emitted Grand / Major / Minor for it would show the wrong labels, the
 * wrong colours, and no artwork at all.
 */
const PREMIUM_LINK_LEVEL_NAMES: Record<number, string> = {
    [JACKPOT_LEVEL_IDS.GRAND]: 'Super',
    [JACKPOT_LEVEL_IDS.MAJOR]: 'Middle',
    [JACKPOT_LEVEL_IDS.MINOR]: 'Mini',
};

/** The level vocabulary this jackpot's real statistics would use. */
const levelNamesFor = (jackpotName: string): Record<number, string> =>
    isPremiumLinkJackpot(jackpotName) ? PREMIUM_LINK_LEVEL_NAMES : LEVEL_NAME_BY_ID;

/** Fallback level set for a jackpot whose poll entry lists no usable level ids. */
const DEFAULT_LEVEL_IDS: number[] = [
    JACKPOT_LEVEL_IDS.GRAND,
    JACKPOT_LEVEL_IDS.MAJOR,
    JACKPOT_LEVEL_IDS.MINOR,
    JACKPOT_LEVEL_IDS.MINI,
];

/**
 * Plausible payout band per level, in MAJOR units of the jackpot's own currency. Grand pays
 * roughly 10x Major, Major 10x Minor, and so on — the same order of magnitude the real families
 * show, so the header's "biggest win" and the TOP 10 ordering look like production data.
 */
const AMOUNT_BAND_BY_LEVEL_ID: Record<number, { min: number; max: number }> = {
    [JACKPOT_LEVEL_IDS.GRAND]: { min: 18_000, max: 140_000 },
    [JACKPOT_LEVEL_IDS.MAJOR]: { min: 1_800, max: 14_000 },
    [JACKPOT_LEVEL_IDS.MINOR]: { min: 180, max: 1_400 },
    [JACKPOT_LEVEL_IDS.MINI]: { min: 18, max: 140 },
};

/** Band used for a level id outside the four-level model. */
const FALLBACK_AMOUNT_BAND = { min: 50, max: 900 };

/** How far back generated wins are spread. Wide enough that the date column shows real variety. */
const WIN_WINDOW_DAYS = 45;

/** How many distinct games a generated jackpot draws its wins from. */
const GAME_POOL_SIZE = 8;

export interface JackpotWinsMockInput {
    /** Jackpot instance name, exactly as `jackpotsFromAPI` keys it. Also the PRNG seed. */
    jackpotName: string;
    /** Rows generated for each populated level. */
    winsPerLevel: number;
    /**
     * Leave the LAST level of the derived set on zero wins, so QA sees a "LEVEL 0" chip and, on
     * selecting it, the drawer's "nobody has won yet" placeholder without extra setup. Ignored for
     * a single-level jackpot: zeroing its only level just empties the drawer.
     */
    includeZeroWinLevel: boolean;
    /**
     * Level ids to mock — the caller passes only the tiers this jackpot actually pays out on, so a
     * family like Bell Link (GRAND/MAJOR statistics, MINOR/MINI tiers with none) is not given
     * invented history. Empty falls back to Grand/Major/Minor/Mini.
     */
    pollLevelIds: number[];
    /**
     * Currency of every generated row — used as BOTH the displayed (player) currency and the
     * ordering currency, since a synthetic win has no separate player to differ from.
     */
    currency: string;
    /** Game keys the jackpot's poll entry lists, if any. Filtered against `supportedGames`. */
    jackpotGameKeys: string[];
}

/* ── Deterministic PRNG ─────────────────────────────────────────── */

/**
 * mulberry32 seeded from a string. Deterministic on purpose: regenerating for the same jackpot
 * reproduces the same rows, so a QA bug report is reproducible from the jackpot name alone.
 * (Timestamps are relative to "now", so only they shift between runs.)
 */
function createRandom(seed: string): () => number {
    let hash = 0x811c9dc5;
    for (let i = 0; i < seed.length; i++) {
        hash ^= seed.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    let state = hash >>> 0;
    return () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const randomInt = (random: () => number, min: number, max: number): number =>
    min + Math.floor(random() * (max - min + 1));

/** Two digits, '*', two digits — exactly how BE masks `casinoPlayerId` (e.g. '06*65'). */
const maskedPlayerId = (random: () => number): string => {
    const head = String(randomInt(random, 0, 99)).padStart(2, '0');
    const tail = String(randomInt(random, 0, 99)).padStart(2, '0');
    return head + '*' + tail;
};

/* ── Game pool ──────────────────────────────────────────────────── */

/**
 * Game keys the generated wins are attributed to. Preference order:
 *   1. the jackpot's own `games` list, intersected with `supportedGames` — the realistic case;
 *   2. any loaded games, when the jackpot lists none this env knows about.
 *
 * Returns an empty array only when `supportedGames` itself is empty, which the caller reports as
 * "games not loaded yet" rather than generating rows with unrenderable thumbnails.
 */
function buildGamePool(jackpotGameKeys: string[]): Array<{ gameKey: string; gameName: string }> {
    const supportedGames = useAppStore.getState().supportedGames;

    const own = jackpotGameKeys.filter((gameKey) => supportedGames[gameKey] !== undefined);
    // Sorted so the fallback slice is stable across generates rather than object-key order.
    const source = own.length > 0 ? own : Object.keys(supportedGames).sort();

    return source
        .slice(0, GAME_POOL_SIZE)
        .map((gameKey) => ({ gameKey, gameName: supportedGames[gameKey]?.name ?? gameKey }));
}

/* ── Level building ─────────────────────────────────────────────── */

/**
 * The levels a mock is generated for, in display order.
 *
 * Derived from the jackpot's OWN poll entry where possible, so an instance with fewer tiers does
 * not get a fabricated four-level drawer. Ids outside the four-level model
 * are dropped — they have no name in the drawer's vocabulary and would render as an unranked tab
 * with no wordmark. If nothing survives, fall back to the full four-level set.
 */
function resolveLevelIds(pollLevelIds: number[]): number[] {
    const known = DEFAULT_LEVEL_IDS.filter((levelId) => pollLevelIds.includes(levelId));
    return known.length > 0 ? known : DEFAULT_LEVEL_IDS;
}

function buildLevel(
    levelId: number,
    winCount: number,
    input: JackpotWinsMockInput,
    gamePool: Array<{ gameKey: string; gameName: string }>,
): IJackpotWinLevel {
    const levelName = levelNamesFor(input.jackpotName)[levelId] ?? LEVEL_NAME_BY_ID[levelId] ?? 'Grand';
    const band = AMOUNT_BAND_BY_LEVEL_ID[levelId] ?? FALLBACK_AMOUNT_BAND;
    const random = createRandom(input.jackpotName + ':' + String(levelId));

    // Strictly descending timestamps: index 0 is the most recent win. Each step is a random slice
    // of the window, so the spacing looks organic instead of evenly ruled.
    const windowMs = WIN_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    const averageStepMs = winCount > 0 ? windowMs / winCount : 0;
    let cursorMs = Date.now() - randomInt(random, 0, Math.max(1, Math.floor(averageStepMs)));

    const rows: IJackpotWinRow[] = [];
    for (let index = 0; index < winCount; index++) {
        const game = gamePool[index % gamePool.length];
        const playerId = maskedPlayerId(random);
        // Whole minor units, exactly like BE — `formatCurrencyValue` divides once, at render.
        const amountMinor = randomInt(random, band.min * JACKPOT_WIN_MINOR_UNITS, band.max * JACKPOT_WIN_MINOR_UNITS);
        const wonAt = new Date(cursorMs).toISOString();

        rows.push({
            // Same natural key the real mapper composes: level name, timestamp, masked player,
            // minor-unit amount. Unique here because timestamps within a level are strictly
            // descending and level names are unique across the payload.
            id: levelName + ':' + wonAt + ':' + playerId + ':' + String(amountMinor),
            playerId,
            levelId,
            levelName,
            amount: amountMinor,
            currency: input.currency,
            sortAmount: amountMinor,
            wonAt,
            gameKey: game.gameKey,
            gameName: game.gameName,
        });

        cursorMs -= randomInt(random, 1, Math.max(2, Math.floor(averageStepMs)));
    }

    return {
        levelId,
        levelName,
        // The counter matches the number of generated rows, so the footer chip count is exactly
        // what the list shows. (Real BE reports a LIFETIME figure that is usually larger; matching
        // is the more useful behaviour for QA, who need the two to be comparable.)
        totalWins: rows.length,
        // Already newest-first by construction; sorted anyway so the contract holds even if the
        // generation order above is ever changed.
        lastWins: rows
            .slice(0, JACKPOT_STATISTICS_LAST_WINS_LIMIT)
            .sort((a, b) => new Date(b.wonAt).getTime() - new Date(a.wonAt).getTime()),
        topWins: rows
            .slice()
            .sort((a, b) => b.amount - a.amount)
            .slice(0, JACKPOT_STATISTICS_TOP_WINS_LIMIT),
    };
}

/**
 * Build a full synthetic win-statistics payload for one jackpot.
 *
 * Returns an empty payload when no games are loaded — rows without a resolvable `gameKey` would
 * render broken thumbnails and a dead launch button.
 */
export function buildJackpotWinsMock(input: JackpotWinsMockInput): IJackpotWinsData {
    const gamePool = buildGamePool(input.jackpotGameKeys);
    if (gamePool.length === 0) {
        return { levels: [] };
    }

    const levelIds = resolveLevelIds(input.pollLevelIds);
    const winsPerLevel = Math.max(0, Math.floor(input.winsPerLevel));
    // Needs another level left over to be worth anything — see `includeZeroWinLevel`.
    const zeroWinLevelId = input.includeZeroWinLevel && levelIds.length > 1 ? levelIds[levelIds.length - 1] : null;

    const levels: IJackpotWinLevel[] = levelIds.map((levelId) =>
        buildLevel(levelId, levelId === zeroWinLevelId ? 0 : winsPerLevel, input, gamePool),
    );

    return { levels };
}
